import React from "react";
import { CheckCircle, Clock, ChevronLeft } from "lucide-react";
import { motion } from "framer-motion";

const TASK_COLORS = {
  buy_fvt: "#c8f135",
  story_or_tweet: "#ff3dac",
  repost: "#3dffea",
  tag_friends: "#ff8c00",
  like_social: "#a78bfa",
  likes_on_repost: "#60a5fa",
};

const TASK_EMOJIS = {
  buy_fvt: "🎟️",
  story_or_tweet: "📣",
  repost: "🔁",
  tag_friends: "👥",
  like_social: "❤️",
  likes_on_repost: "⭐",
};

const TASK_LABELS = {
  buy_fvt: "רכישה",
  story_or_tweet: "סטורי / ציוץ",
  repost: "שיתוף",
  tag_friends: "תיוג חברים",
  like_social: "לייק",
  likes_on_repost: "לייקים",
};

export default function TaskCard({ task, onSelect, completed, status = "open", index = 0, themeVars = {} }) {
  const color = TASK_COLORS[task.task_type] || "#c8f135";
  const emoji = TASK_EMOJIS[task.task_type] || "✅";
  const typeLabel = TASK_LABELS[task.task_type] || "";
  const displayPoints = task.max_points || task.points_per_action;
  const isPendingReview = status === "pending_review";
  const isLocked = completed || isPendingReview || !onSelect;
  const isLight = themeVars.light;

  // Dark-first design inspired by ticket apps
  const cardBg = isLight
    ? (completed ? "#f3f4f6" : isPendingReview ? "#f0f7ff" : "#ffffff")
    : (completed ? "#0d0d0d" : isPendingReview ? "#0d1520" : "#161616");

  const cardBorderColor = isLight
    ? (completed ? "#e5e7eb" : isPendingReview ? "#bfdbfe" : "#e5e7eb")
    : (completed ? "#1f1f1f" : isPendingReview ? "#1e3a5f" : "#2a2a2a");

  const titleColor = isLight
    ? (completed ? "#9ca3af" : "#111111")
    : (completed ? "#444" : "#ffffff");

  const descColor = isLight ? "#9ca3af" : "rgba(255,255,255,0.38)";

  const accentColor = completed ? (isLight ? "#d1d5db" : "#2a2a2a") : isPendingReview ? "#60a5fa" : color;

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.07, duration: 0.32 }}
      whileHover={!isLocked ? { scale: 1.012, y: -1 } : {}}
      whileTap={!isLocked ? { scale: 0.988 } : {}}
      onClick={!isLocked ? onSelect : undefined}
      className={`relative rounded-2xl flex ${!isLocked ? "cursor-pointer" : ""}`}
      style={{
        background: cardBg,
        border: `1.5px solid ${cardBorderColor}`,
        boxShadow: !isLocked && !isLight ? `0 2px 20px ${color}12` : "none",
      }}
    >
      {/* Left accent bar (ticket stub style) */}
      <div
        className="w-1.5 flex-shrink-0 rounded-r-full"
        style={{
          background: completed
            ? (isLight ? "#e5e7eb" : "#1f1f1f")
            : isPendingReview
              ? "linear-gradient(180deg, #60a5fa, #3b82f6)"
              : `linear-gradient(180deg, ${color}, ${color}99)`,
        }}
      />

      {/* Main content */}
      <div className="flex items-center gap-3 flex-1 p-3 sm:p-4 min-w-0">
        {/* Icon badge */}
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 text-lg"
          style={{
            background: completed
              ? (isLight ? "#f0f0f0" : "#1a1a1a")
              : `${accentColor}18`,
            border: `1.5px solid ${completed ? (isLight ? "#e0e0e0" : "#242424") : `${accentColor}40`}`,
          }}
        >
          {completed
            ? <CheckCircle className="w-4.5 h-4.5" style={{ color: isLight ? "#d1d5db" : "#333" }} />
            : isPendingReview
              ? <Clock className="w-4 h-4" style={{ color: "#60a5fa" }} />
              : <span className="text-base">{emoji}</span>
          }
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          {/* Type pill */}
          <div className="flex items-center gap-2 mb-1">
            <span
              className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full"
              style={{
                background: completed
                  ? (isLight ? "#f0f0f0" : "#1a1a1a")
                  : `${accentColor}20`,
                color: completed
                  ? (isLight ? "#ccc" : "#333")
                  : accentColor,
              }}
            >
              {typeLabel}
            </span>
            {isPendingReview && (
              <span className="text-[10px] font-black px-2 py-0.5 rounded-full"
                style={{ background: "rgba(96,165,250,0.15)", color: "#60a5fa" }}>
                בבדיקה
              </span>
            )}
          </div>
          <p className="font-black text-sm leading-snug" style={{ color: titleColor }}>
            {task.title}
          </p>
          <p className="text-xs mt-0.5 truncate" style={{ color: descColor }}>
            {task.description}
          </p>
        </div>

        {/* Points + CTA */}
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          {/* Points badge */}
          <div
            className="text-center px-2 sm:px-3 py-1.5 rounded-xl"
            style={{
              background: completed
                ? (isLight ? "#f0f0f0" : "#1a1a1a")
                : `${accentColor}18`,
              border: `1px solid ${completed ? (isLight ? "#e5e7eb" : "#222") : `${accentColor}35`}`,
            }}
          >
            <span
              className="font-black block leading-none"
              style={{
                fontSize: displayPoints >= 100 ? "1.3rem" : "1.1rem",
                color: completed ? (isLight ? "#ccc" : "#333") : accentColor,
              }}
            >
              +{completed ? (task.points_earned ?? displayPoints) : displayPoints}
            </span>
            <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: descColor }}>נק'</span>
          </div>

          {/* Action button */}
          {!completed && (
            <button
              type="button"
              disabled={isPendingReview || !onSelect}
              onClick={(e) => { e.stopPropagation(); if (!isPendingReview) onSelect?.(); }}
              className="flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-lg text-[11px] sm:text-xs font-black transition-all whitespace-nowrap"
              style={{
                background: isPendingReview
                  ? "transparent"
                  : accentColor,
                color: isPendingReview
                  ? "#60a5fa"
                  : (accentColor === "#c8f135" || accentColor === "#3dffea" || accentColor === "#ff8c00") ? "#000" : "#000",
                border: isPendingReview ? "1px solid rgba(96,165,250,0.3)" : "none",
                opacity: isPendingReview ? 0.8 : 1,
              }}
            >
              {isPendingReview ? "ממתין" : "בצע"}
              {!isPendingReview && <ChevronLeft className="w-3 h-3" />}
            </button>
          )}
        </div>
      </div>

      {/* Dashed ticket perforation line */}
      {!completed && !isLight && (
        <div
          className="absolute right-[4.5rem] top-0 bottom-0 w-px"
          style={{ background: "repeating-linear-gradient(180deg, transparent 0, transparent 4px, #2a2a2a 4px, #2a2a2a 8px)" }}
        />
      )}
    </motion.div>
  );
}